/**
 * Dichtestufe zur Laufzeit (REQ-20 §6).
 *
 * Wie `useTheme`: Das Inline-Skript aus `index.html` hat `data-density` beim
 * Laden bereits gesetzt, der Hook liest den Ist-Zustand aus dem DOM und
 * schreibt nur bei einer Änderung.
 *
 * `normal` ist die Standarddichte und trägt **kein** Attribut — das Inline-Skript
 * und `ui/_tokens.scss` erwarten das so (REQ-19 §4.3).
 */

import { useCallback, useState } from 'react';

import { DENSITY_STORAGE_KEY } from '@/config/appConfig';

export type Density = 'compact' | 'normal' | 'cozy';

export interface DensityState {
  density: Density;
  /** Eigenschaft statt Methode: Der Rückgabewert wird destrukturiert. */
  setDensity: (value: Density) => void;
}

/** Ist-Zustand am `<html>`-Element; ohne Attribut gilt `normal`. */
function currentDensity(): Density {
  const value = document.documentElement.dataset.density;
  return value === 'compact' || value === 'cozy' ? value : 'normal';
}

export function useDensity(): DensityState {
  const [density, setState] = useState<Density>(currentDensity);

  const setDensity = useCallback((value: Density): void => {
    if (currentDensity() === value) {
      return;
    }

    if (value === 'normal') {
      document.documentElement.removeAttribute('data-density');
    } else {
      document.documentElement.dataset.density = value;
    }

    try {
      window.localStorage.setItem(DENSITY_STORAGE_KEY, value);
    } catch {
      // Kein localStorage (privater Modus) — die Umschaltung wirkt nur für diese Sitzung.
    }
    setState(value);
  }, []);

  return { density, setDensity };
}
